import { useTranslation } from "react-i18next";
import { FormSection } from "../../../components/FormSection";
import { useIsAdmin } from "../../../hooks/useIsAdmin";
import { IntegrationType } from "@KassaroAB/shared/types/enums/IntegrationType";
import { getEffectiveBookkeepingMapping } from "@KassaroAB/shared/utils/bookkeepingDefaultAccounts";
import type { PaymentMethodViva } from "@KassaroAB/shared/types/paymentMethods/paymentMethod";
import { FieldGroupPaymentMethodFields } from "../paymentMethodFieldGroup";
import { usePaymentMethodForm } from "../paymentMethodFormHook";
import {
  PaymentMethodFormContainer,
  type PaymentMethodFormProps,
} from "./PaymentMethodFormContainer";
import { basePaymentMethodValues } from "./basePaymentMethodValues";
import { Fields, IntegrationField } from "./paymentMethodFields";
import { FieldGroupVivaAdminFields } from "./vivaAdminFieldGroup";

export const VivaForm = ({
  formId,
  store,
  paymentMethod,
  paymentMethodId,
  savedTick,
  onSubmit,
  onDirtyChange,
}: PaymentMethodFormProps<Omit<PaymentMethodViva, "id">>) => {
  const { t } = useTranslation();
  const isAdmin = useIsAdmin();
  const mergedAccounts = getEffectiveBookkeepingMapping(
    store.country,
    store.bookkeepingDefaultAccounts,
  );
  const form = usePaymentMethodForm({
    defaultValues: {
      ...basePaymentMethodValues(paymentMethod),
      integrationId: paymentMethod.integrationId ?? "",
      isFastRefundEnabled: paymentMethod.isFastRefundEnabled ?? false,
      multiMerchantId: paymentMethod.multiMerchantId ?? "",
    },
    onSubmit: ({ value }) => onSubmit(value),
  });

  return (
    <PaymentMethodFormContainer
      formId={formId}
      form={form}
      savedTick={savedTick}
      onDirtyChange={onDirtyChange}
    >
      <FieldGroupPaymentMethodFields
        form={form}
        fields={{
          name: "name",
          type: "type",
          bookkeepingAccountId: "bookkeepingAccountId",
          color: "color",
        }}
        defaultAccountNumber={mergedAccounts.businessAccount}
        t={t}
      />
      <FormSection title={t("paymentMethodForm.additionalSettings")}>
        <Fields>
          <form.Field
            name="integrationId"
            validators={{
              onChange: ({ value }) =>
                value ? undefined : t("paymentMethodForm.integrationRequired"),
            }}
          >
            {(field) => (
              <IntegrationField
                label={t("paymentMethodForm.integration")}
                placeholder={t("paymentMethodForm.selectIntegration")}
                chainId={store.chainId}
                integrationTypes={[IntegrationType.VIVA]}
                value={field.state.value}
                onChange={field.handleChange}
                onBlur={field.handleBlur}
                errors={field.state.meta.errors}
              />
            )}
          </form.Field>
        </Fields>
      </FormSection>
      {isAdmin && (
        <FieldGroupVivaAdminFields
          form={form}
          fields={{ multiMerchantId: "multiMerchantId" }}
          storeId={store.id}
          paymentMethodId={paymentMethodId ?? ""}
          t={t}
        />
      )}
    </PaymentMethodFormContainer>
  );
};
